import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type WheelEvent,
} from 'react';
import { INDIA_STATES, projectIndia } from './indiaStates';
import { layerMax, stateMetric } from './aggregateStates';
import type { IndiaMapModel, IndiaStateCode, MapLayerId, StateAggregate } from './types';
import type { WarehouseMapPin, WarehouseMapRow } from './warehouseLayer';

const VIEW_W = 1000;
const VIEW_H = 1100;

function heatColor(t: number, hasData: boolean): string {
  if (!hasData) return 'var(--shell-border)';
  // Sequential teal-cyan (readable in dark + light) — intensity via CSS color-mix
  const p = Math.max(0.12, Math.min(1, t));
  return `color-mix(in srgb, var(--shell-accent) ${Math.round(p * 100)}%, var(--shell-surface))`;
}

/**
 * Interactive India schematic map: centroids + official state markers.
 * Zoom, pan, hover, keyboard.
 */
export function IndiaMapCanvas({
  model,
  layer,
  activeCode,
  hoverCode,
  onHover,
  onSelect,
  heatGated,
  showWarehousePins = false,
  showWarehouseCoverage = false,
  activeWarehouseId = null,
  onSelectWarehouse,
}: {
  model: IndiaMapModel;
  layer: MapLayerId;
  activeCode: IndiaStateCode | null;
  hoverCode: IndiaStateCode | null;
  onHover: (code: IndiaStateCode | null) => void;
  onSelect: (code: IndiaStateCode) => void;
  heatGated: boolean;
  showWarehousePins?: boolean;
  showWarehouseCoverage?: boolean;
  activeWarehouseId?: string | null;
  onSelectWarehouse?: (w: WarehouseMapRow) => void;
}) {
  const rid = useId();
  const svgRef = useRef<SVGSVGElement>(null);
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  const drag = useRef<{ x: number; y: number; tx: number; ty: number } | null>(null);

  const byCode = useMemo(() => {
    const m = new Map<IndiaStateCode, StateAggregate>();
    for (const s of model.states) m.set(s.code, s);
    return m;
  }, [model.states]);

  const maxV = layerMax(model.states, layer) || 1;

  const onWheel = (e: WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setScale((s) => Math.min(4, Math.max(0.75, s * delta)));
  };

  const onPointerDown = (e: ReactPointerEvent) => {
    if (e.button !== 0) return;
    (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
    drag.current = { x: e.clientX, y: e.clientY, tx, ty };
  };
  const onPointerMove = (e: ReactPointerEvent) => {
    if (!drag.current) return;
    setTx(drag.current.tx + (e.clientX - drag.current.x));
    setTy(drag.current.ty + (e.clientY - drag.current.y));
  };
  const onPointerUp = () => {
    drag.current = null;
  };

  const resetView = () => {
    setScale(1);
    setTx(0);
    setTy(0);
  };

  const focusable = model.states.filter((s) => s.hasData || !heatGated);
  const focusCodes = focusable.map((s) => s.code);

  const onKeyNav = useCallback(
    (e: KeyboardEvent, code: IndiaStateCode) => {
      const idx = focusCodes.indexOf(code);
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onSelect(code);
        return;
      }
      if (idx < 0) return;
      let next = idx;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = Math.min(focusCodes.length - 1, idx + 1);
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = Math.max(0, idx - 1);
      if (next !== idx) {
        e.preventDefault();
        const el = document.getElementById(`${rid}-state-${focusCodes[next]}`);
        el?.focus();
        onHover(focusCodes[next]);
      }
    },
    [focusCodes, onHover, onSelect, rid],
  );

  useEffect(() => {
    // prevent page scroll steal when wheel over map
    const el = svgRef.current;
    if (!el) return;
    const handler = (ev: Event) => {
      /* passive false registered via React onWheel preventDefault when possible */
      void ev;
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, []);

  return (
    <div className="relative min-h-[320px] w-full overflow-hidden rounded-[var(--shell-radius-md)] border border-[var(--shell-border)] bg-[var(--shell-context)]">
      <div className="absolute right-2 top-2 z-10 flex gap-1">
        <button
          type="button"
          className="min-h-[36px] min-w-[36px] rounded border border-[var(--shell-border)] bg-[var(--shell-surface)] text-sm text-[var(--shell-fg)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--shell-accent)]"
          onClick={() => setScale((s) => Math.min(4, s * 1.2))}
          aria-label="Zoom in"
        >
          +
        </button>
        <button
          type="button"
          className="min-h-[36px] min-w-[36px] rounded border border-[var(--shell-border)] bg-[var(--shell-surface)] text-sm text-[var(--shell-fg)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--shell-accent)]"
          onClick={() => setScale((s) => Math.max(0.75, s / 1.2))}
          aria-label="Zoom out"
        >
          −
        </button>
        <button
          type="button"
          className="min-h-[36px] rounded border border-[var(--shell-border)] bg-[var(--shell-surface)] px-2 text-xs text-[var(--shell-fg)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--shell-accent)]"
          onClick={resetView}
          aria-label="Reset map view"
        >
          Reset
        </button>
      </div>

      <svg
        ref={svgRef}
        role="img"
        aria-label="India business intelligence map by state"
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        className="h-[min(56vh,520px)] w-full touch-none cursor-grab active:cursor-grabbing"
        onWheel={onWheel}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={() => {
          onPointerUp();
          onHover(null);
        }}
      >
        <defs>
          <filter id={`${rid}-glow`} x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow dx="0" dy="0" stdDeviation="3" floodColor="var(--shell-accent)" floodOpacity="0.45" />
          </filter>
        </defs>
        <g transform={`translate(${tx} ${ty}) scale(${scale})`} style={{ transformOrigin: '500px 550px' }}>
          {/* Schematic India extent frame (not disputed-boundary cartography) */}
          <rect
            x={40}
            y={30}
            width={920}
            height={1020}
            rx={12}
            fill="transparent"
            stroke="var(--shell-border)"
            strokeWidth={1.5}
            strokeDasharray="6 4"
            opacity={0.55}
          />
          <text
            x={500}
            y={70}
            textAnchor="middle"
            fill="var(--shell-fg-subtle)"
            fontSize={18}
            fontFamily="system-ui,sans-serif"
          >
            India · states & UTs
          </text>

          {INDIA_STATES.map((def) => {
            const st = byCode.get(def.code)!;
            const { x, y } = projectIndia(def.lat, def.lon);
            const raw = stateMetric(st, layer);
            const t =
              heatGated || !st.hasData || raw == null || maxV <= 0
                ? 0
                : (raw as number) / maxV;
            const r =
              heatGated || !st.hasData
                ? 7
                : 8 + Math.sqrt(Math.max(0, t)) * 22;
            const selected = activeCode === def.code;
            const hovered = hoverCode === def.code;
            const covered =
              showWarehouseCoverage &&
              model.warehouseCoverageStates.includes(def.code);
            const fill = heatGated
              ? 'var(--shell-border)'
              : heatColor(t, st.hasData);
            const label = st.hasData
              ? `${st.name}: ${layer} ${raw ?? 'n/a'}`
              : `${st.name}: no orders in this period`;

            return (
              <g key={def.code}>
                {covered && (
                  <circle
                    cx={x}
                    cy={y}
                    r={r + 14}
                    fill="none"
                    stroke="var(--shell-accent)"
                    strokeWidth={1.5}
                    strokeDasharray="4 3"
                    opacity={0.55}
                    pointerEvents="none"
                    aria-hidden
                  />
                )}
                <circle
                  id={`${rid}-state-${def.code}`}
                  cx={x}
                  cy={y}
                  r={r}
                  fill={fill}
                  stroke={
                    selected || hovered
                      ? 'var(--shell-accent)'
                      : 'var(--shell-fg-subtle)'
                  }
                  strokeWidth={selected ? 3 : hovered ? 2 : 1}
                  opacity={heatGated ? 0.35 : st.hasData ? 0.95 : 0.4}
                  filter={selected ? `url(#${rid}-glow)` : undefined}
                  tabIndex={0}
                  role="button"
                  aria-label={label}
                  aria-pressed={selected}
                  style={{ cursor: 'pointer', outline: 'none' }}
                  onMouseEnter={() => onHover(def.code)}
                  onFocus={() => onHover(def.code)}
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelect(def.code);
                  }}
                  onKeyDown={(e) => onKeyNav(e as unknown as KeyboardEvent, def.code)}
                />
                {(hovered || selected || (st.hasData && scale > 1.3)) && (
                  <text
                    x={x}
                    y={y - r - 6}
                    textAnchor="middle"
                    fill="var(--shell-fg)"
                    fontSize={11}
                    fontFamily="system-ui,sans-serif"
                    pointerEvents="none"
                  >
                    {def.code}
                  </text>
                )}
              </g>
            );
          })}

          {showWarehousePins &&
            model.warehousePins.map((pin: WarehouseMapPin) => {
              const { x, y } = projectIndia(pin.latitude, pin.longitude);
              const active = activeWarehouseId === pin.id;
              const al = `${pin.code} ${pin.name}, ${pin.city || 'city N/A'}, ${pin.stateLabel || 'state N/A'}`;
              return (
                <g key={pin.id}>
                  <circle
                    cx={x}
                    cy={y}
                    r={active ? 9 : 6}
                    fill="var(--shell-surface)"
                    stroke="var(--shell-accent)"
                    strokeWidth={active ? 2.5 : 2}
                    role="button"
                    tabIndex={0}
                    aria-label={al}
                    aria-pressed={active}
                    style={{ cursor: 'pointer', outline: 'none' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelectWarehouse?.(pin);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onSelectWarehouse?.(pin);
                      }
                    }}
                  />
                  <text
                    x={x}
                    y={y + (active ? 16 : 14)}
                    textAnchor="middle"
                    fill="var(--shell-fg)"
                    fontSize={10}
                    fontFamily="ui-monospace,monospace"
                    pointerEvents="none"
                  >
                    {pin.code}
                  </text>
                </g>
              );
            })}
        </g>
      </svg>
    </div>
  );
}
